import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Shield, UserCheck, UserX, Trash2 } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserManagementPage({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/users/${userId}/role?new_role=${newRole}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("User role updated successfully");
      fetchUsers();
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-700';
      case 'homecell_leader':
        return 'bg-blue-100 text-blue-700';
      case 'disciple':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin':
        return <Shield className="h-4 w-4" />;
      case 'homecell_leader':
        return <UserCheck className="h-4 w-4" />;
      case 'disciple':
        return <Users className="h-4 w-4" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'homecell_leader':
        return 'Homecell Leader';
      case 'disciple':
        return 'Disciple';
      default:
        return role;
    }
  };

  const roleOptions = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'homecell_leader', label: 'Homecell Leader' },
    { value: 'disciple', label: 'Disciple' }
  ];

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  const stats = {
    total: users.length,
    super_admin: users.filter(u => u.role === 'super_admin').length,
    homecell_leader: users.filter(u => u.role === 'homecell_leader').length,
    disciple: users.filter(u => u.role === 'disciple').length
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <span className="gradient-text">User Management</span>
        </h1>
        <p className="text-gray-600">Manage user roles and permissions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Total Users</p>
              <p className="text-3xl font-bold" data-testid="total-users">{stats.total}</p>
            </div>
            <Users className="h-12 w-12 text-white/40" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Super Admins</p>
              <p className="text-3xl font-bold text-gray-800">{stats.super_admin}</p>
            </div>
            <Shield className="h-12 w-12 text-purple-500" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Homecell Leaders</p>
              <p className="text-3xl font-bold text-gray-800">{stats.homecell_leader}</p>
            </div>
            <UserCheck className="h-12 w-12 text-blue-500" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Disciples</p>
              <p className="text-3xl font-bold text-gray-800">{stats.disciple}</p>
            </div>
            <Users className="h-12 w-12 text-green-500" />
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-bold mb-4">All Users</h2>
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid={`user-card-${u.id}`}>
              <div className="flex items-center gap-4 flex-1">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center">
                  <span className="text-white text-lg font-bold">{u.name[0]}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{u.name}</h3>
                  <p className="text-sm text-gray-600">{u.email}</p>
                  <p className="text-xs text-gray-500">Joined {format(new Date(u.created_at), "MMM d, yyyy")}</p>
                </div>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-full font-medium ${getRoleColor(u.role)}`}>
                  {getRoleIcon(u.role)}
                  <span>{getRoleLabel(u.role)}</span>
                </div>
              </div>

              {u.id !== user.id && (
                <div className="flex gap-2 ml-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        data-testid={`change-role-${u.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedUser(u)}
                      >
                        Change Role
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Change Role for {u.name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Current Role</Label>
                          <p className="text-gray-600">{getRoleLabel(u.role)}</p>
                        </div>
                        <div>
                          <Label>Select New Role</Label>
                          <div className="space-y-2 mt-2">
                            {roleOptions.map((option) => (
                              <button
                                key={option.value}
                                data-testid={`role-option-${option.value}`}
                                onClick={() => handleRoleChange(u.id, option.value)}
                                disabled={option.value === u.role}
                                className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                                  option.value === u.role
                                    ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                                    : 'border-gray-200 hover:border-purple-500 hover:bg-purple-50'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {getRoleIcon(option.value)}
                                  <span className="font-medium">{option.label}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Button
                    data-testid={`delete-user-${u.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteUser(u.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
